// src/contexts/InstitutionContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Institution, SubInstitution, getCurrentInstitution } from '../config/institutions';

interface InstitutionContextType {
  institution: Institution;
  selectedSubInstitution: SubInstitution | null;
  setSelectedSubInstitution: (subInstitution: SubInstitution | null) => void;
  hasSubInstitutions: boolean;
}

const InstitutionContext = createContext<InstitutionContextType | undefined>(undefined);

export const InstitutionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [institution, setInstitution] = useState<Institution>(() => {
    // Başlangıç durumunda ana domain kontrolü yap
    const currentInst = getCurrentInstitution();

    // Ana domain için özel durum
    if (window.location.hostname === "teknokarne.net" ||
      window.location.hostname === "www.teknokarne.net") {
      // Ana domain için kurum kodunu boş bırak
      return {
        ...currentInst,
        code: ""
      };
    }

    return currentInst;
  });

  const [selectedSubInstitution, setSelectedSubInstitution] = useState<SubInstitution | null>(null);

  // Sayfa yüklendiğinde ve URL değiştiğinde kurumu güncelle
  useEffect(() => {
    const updateInstitution = () => {
      let currentInstitution = getCurrentInstitution();

      // Ana domain için özel durum
      if (window.location.hostname === "teknokarne.net" ||
        window.location.hostname === "www.teknokarne.net") {
        currentInstitution = {
          ...currentInstitution,
          code: ""
        };
      }

      setInstitution(currentInstitution);

      // URL'den alt kurum ID'sini al (örneğin ?subId=ibrahimari-kurs)
      const urlParams = new URLSearchParams(window.location.search);
      const subId = urlParams.get('subId');

      // LocalStorage'dan kayıtlı alt kurum bilgisini al
      const savedSubInstitutionId = localStorage.getItem(`${currentInstitution.id}_selectedSubInstitution`);

      // Alt kurumları olan kurumlar için alt kurum seçimini ayarla
      if (currentInstitution.subInstitutions && currentInstitution.subInstitutions.length > 0) {
        // Önce URL'den, sonra localStorage'dan, yoksa ilk alt kurumu seç
        if (subId) {
          const subInst = currentInstitution.subInstitutions.find(s => s.id === subId) || null;
          setSelectedSubInstitution(subInst);
          if (subInst) {
            localStorage.setItem(`${currentInstitution.id}_selectedSubInstitution`, subInst.id);
          }
        } else if (savedSubInstitutionId) {
          const subInst = currentInstitution.subInstitutions.find(s => s.id === savedSubInstitutionId) || null;
          setSelectedSubInstitution(subInst);
        } else {
          setSelectedSubInstitution(currentInstitution.subInstitutions[0]);
          localStorage.setItem(`${currentInstitution.id}_selectedSubInstitution`, currentInstitution.subInstitutions[0].id);
        }
      } else {
        setSelectedSubInstitution(null);
      }
    };

    updateInstitution();
    window.addEventListener('popstate', updateInstitution);

    return () => {
      window.removeEventListener('popstate', updateInstitution);
    };
  }, []);

  // Alt kurum değiştiğinde localStorage'a kaydet
  const handleSetSelectedSubInstitution = (subInstitution: SubInstitution | null) => {
    console.log("Setting selected sub-institution:", subInstitution?.name);
    setSelectedSubInstitution(subInstitution);

    if (subInstitution) {
      localStorage.setItem(`${institution.id}_selectedSubInstitution`, subInstitution.id);
    } else {
      localStorage.removeItem(`${institution.id}_selectedSubInstitution`);
    }

    // Trigger React Query cache invalidation for queries that depend on institution code
    // This is a workaround to ensure components refetch when institution changes
    setTimeout(() => {
      // Dispatch a custom event that components can listen for
      window.dispatchEvent(new CustomEvent('institutionChanged', {
        detail: {
          institutionCode: institution.code,
          subInstitutionCode: subInstitution?.code
        }
      }));
    }, 0);
  };

  return (
    <InstitutionContext.Provider
      value={{
        institution,
        selectedSubInstitution,
        setSelectedSubInstitution: handleSetSelectedSubInstitution,
        hasSubInstitutions: Boolean(institution.subInstitutions && institution.subInstitutions.length > 0)
      }}
    >
      {children}
    </InstitutionContext.Provider>
  );
};

export const useInstitution = () => {
  const context = useContext(InstitutionContext);
  if (context === undefined) {
    throw new Error('useInstitution must be used within an InstitutionProvider');
  }
  return context;
};